/**
 * TrialVisitors.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Every tryout visitor the trial service knows about, and the two edits a
 * support agent needs: change a visitor's standing, or reopen a blocked trial.
 *
 * ── WHY THIS IS ITS OWN PAGE ─────────────────────────────────────────────────
 *
 * It used to be a section at the bottom of Pricing & Credits called "Blocked
 * trial visitors", which could only ever show the blocked ones. That is the
 * wrong starting point for the job: an agent taking a call does not yet know
 * whether the person is blocked, so the first question — "are they even in
 * here?" — was unanswerable from the screen. This lists everybody and lets the
 * agent narrow down.
 *
 * The old section has been removed rather than left in place. Two screens
 * editing the same rows means a Save in one leaves the other showing values
 * that are no longer true.
 *
 * ── IT IS STILL A TEST TOOL ──────────────────────────────────────────────────
 *
 * The endpoints behind it exist only on a trial service with
 * TrialAdmin:Enabled set, and the page hides its table entirely when the two
 * environment settings are absent — see trialAdminService, which also explains
 * why the admin key in a React bundle is a deployment gate and not a secret.
 */
import React, { useEffect, useState } from 'react';
import * as trialApi from '../services/trialAdminService';

const inp: React.CSSProperties = {
  width: 90, padding: '5px 8px', border: '1px solid #cdd6e0', borderRadius: 6, fontSize: 13,
};
const txt: React.CSSProperties = { ...inp, width: 150 };
const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: '.04em', color: '#6b7684', borderBottom: '1px solid #e3e9ef', fontWeight: 600,
};
const td: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid #eef2f6', fontSize: 13 };

const STATUSES: trialApi.TrialStatus[] =
  ['Active', 'Warning', 'RegistrationRequired', 'Blocked', 'Registered'];

/**
 * The ladder, in the order a visitor walks it.
 *
 * Sorting by status ALPHABETICALLY would put Active, Blocked, Registered,
 * RegistrationRequired, Warning — which reads as noise. A support agent thinks
 * in severity, so that is the order, and it is defined once here rather than
 * inferred from the enum's declaration order in another repository.
 */
const STATUS_RANK: Record<trialApi.TrialStatus, number> = {
  Active: 0,
  Warning: 1,
  RegistrationRequired: 2,
  Blocked: 3,
  Registered: 4,
};

/** The status pill's colours. Paired with the label, never colour alone. */
const STATUS_TONE: Record<trialApi.TrialStatus, { fg: string; bg: string }> = {
  Active:               { fg: '#0b7a3b', bg: '#e6f6ec' },
  Warning:              { fg: '#8a5a00', bg: '#fdf1dc' },
  RegistrationRequired: { fg: '#9a4a1e', bg: '#fdeee5' },
  Blocked:              { fg: '#a32020', bg: '#fdeaea' },
  Registered:           { fg: '#1b4f9c', bg: '#e8f0fc' },
};

/** Every column that can be sorted, and how to read a value out of a row. */
type SortKey =
  | 'trialId' | 'platform' | 'browser' | 'visitCount'
  | 'status' | 'lastVisit' | 'updatedOn' | 'country' | 'city';

interface Column {
  key: SortKey;
  label: string;
  /** Sort value. Numbers sort numerically; strings case-insensitively. */
  value: (row: trialApi.TrialAdminVisitor) => string | number;
  /** Default direction when the column is first clicked. */
  descFirst?: boolean;
}

/**
 * One table of columns, used for BOTH the header and the sort.
 *
 * The alternative — a header row in JSX and a switch in the comparator — is two
 * lists that drift: a renamed column keeps sorting by the old field, and a
 * removed one leaves a dead branch nothing calls. Here a column cannot exist
 * without saying how it sorts.
 */
const COLUMNS: Column[] = [
  { key: 'trialId',    label: 'Trial',          value: r => r.trialId.toLowerCase() },
  { key: 'platform',   label: 'Platform',       value: r => (r.platform ?? '').toLowerCase() },
  { key: 'browser',    label: 'Browser',        value: r => (r.browser ?? '').toLowerCase() },
  { key: 'visitCount', label: 'Visits',         value: r => r.visitCount, descFirst: true },
  { key: 'status',     label: 'Status',         value: r => STATUS_RANK[r.status] ?? 99 },
  { key: 'lastVisit',  label: 'Session started', value: r => Date.parse(r.lastVisit) || 0, descFirst: true },
  { key: 'updatedOn',  label: 'Last seen',      value: r => Date.parse(r.updatedOn) || 0, descFirst: true },
  { key: 'country',    label: 'Country',        value: r => (r.countryName ?? '').toLowerCase() },
  { key: 'city',       label: 'City',           value: r => (r.city ?? '').toLowerCase() },
];

/** What the user has typed but not yet saved, keyed by trial id. */
interface Draft { visitCount: number; status: trialApi.TrialStatus; }

export const TrialVisitors: React.FC = () => {
  const [rows, setRows] = useState<trialApi.TrialAdminVisitor[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  /** Which column is sorted, and which way. Status descending is the useful default. */
  const [sortKey, setSortKey] = useState<SortKey>('updatedOn');
  const [sortDesc, setSortDesc] = useState(true);

  /** Narrow to one standing. 'All' is not a status, so it is modelled separately. */
  const [statusFilter, setStatusFilter] = useState<trialApi.TrialStatus | 'All'>('All');

  const configured = trialApi.isTrialAdminConfigured();

  const flash = (m: string) => { setErr(''); setMsg(m); setTimeout(() => setMsg(''), 2500); };
  const fail = (e: any) => setErr(String(e?.message || e));

  const load = React.useCallback(() => {
    if (!configured) return;
    setLoading(true);
    trialApi.getVisitors()
      .then(visitors => {
        setRows(visitors);
        // Drafts are rebuilt from the server response on every load, so a reload
        // discards half-finished edits rather than leaving a stale number in a
        // box next to a value that has since changed underneath it.
        setDrafts(Object.fromEntries(visitors.map(v =>
          [v.trialId, { visitCount: v.visitCount, status: v.status }])));
        setLoaded(true);
      })
      .catch(e => setErr(String(e?.message || e)))
      .finally(() => setLoading(false));
  }, [configured]);

  useEffect(() => { load(); }, [load]);

  const setDraft = (trialId: string, patch: Partial<Draft>) =>
    setDrafts(d => ({ ...d, [trialId]: { ...d[trialId], ...patch } }));

  const save = (trialId: string) => {
    const draft = drafts[trialId];
    if (!draft) return;

    setSaving(trialId);
    trialApi.updateVisitor(trialId, { visitCount: Number(draft.visitCount), status: draft.status })
      .then(updated => {
        flash(`Saved. ${shortId(trialId)} is now ${updated.status}.`);
        // Reload rather than patching in place: an edit can move a row's
        // position under the current sort, and the server is the only thing that
        // knows what UpdatedOn became.
        load();
      })
      .catch(fail)
      .finally(() => setSaving(null));
  };

  /**
   * One-click reset to the state a fresh visitor is in — visits, standing AND
   * the day window.
   *
   * Delegates to reopenVisitor rather than composing the three fields here. It
   * used to compose two of them, and the missing third (the calendar) meant any
   * trial older than Trial:MaxTrialDays was re-blocked on the visitor's next
   * request while this button reported success.
   */
  const reopen = (trialId: string) => {
    setDraft(trialId, { visitCount: 0, status: 'Active' });
    setSaving(trialId);
    trialApi.reopenVisitor(trialId)
      .then(() => {
        flash(`${shortId(trialId)} reopened — visit count 0, status Active, trial days restarted.`);
        load();
      })
      .catch(fail)
      .finally(() => setSaving(null));
  };

  const dirty = (row: trialApi.TrialAdminVisitor) => {
    const draft = drafts[row.trialId];
    return !!draft && (Number(draft.visitCount) !== row.visitCount || draft.status !== row.status);
  };

  /** Clicking the sorted column flips direction; clicking another switches to it. */
  const sortBy = (column: Column) => {
    if (column.key === sortKey) { setSortDesc(d => !d); return; }
    setSortKey(column.key);
    setSortDesc(!!column.descFirst);
  };

  const column = COLUMNS.find(c => c.key === sortKey) ?? COLUMNS[0];

  const visible = rows
    .filter(row => statusFilter === 'All' || row.status === statusFilter)
    .slice()   // sort() mutates, and `rows` is state
    .sort((a, b) => {
      const left = column.value(a);
      const right = column.value(b);
      // Ties fall back to the trial id so the order is stable rather than
      // dependent on the browser's sort implementation — a list that reshuffles
      // its equal rows on every re-render is maddening to read.
      const primary = left < right ? -1 : left > right ? 1 : a.trialId.localeCompare(b.trialId);
      return sortDesc ? -primary : primary;
    });

  const counts = STATUSES.map(s => ({ status: s, n: rows.filter(r => r.status === s).length }));

  return (
    <div>
      <section className="ov-section">
        <div className="ov-section__head">
          <h2 className="ov-section__title">Trial visitors</h2>
          <button className="btn-small" onClick={load} disabled={loading || !configured}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {!configured && (
          <p style={{ color: '#6b7684', fontSize: 13, margin: 0 }}>
            Not configured. This page needs <code>REACT_APP_TRIAL_API_URL</code> and{' '}
            <code>REACT_APP_TRIAL_ADMIN_KEY</code>, and a trial service running with{' '}
            <code>TrialAdmin:Enabled</code> set. It is a test tool — do not point it at production.
          </p>
        )}

        {configured && (
          <>
            <p style={{ color: '#6b7684', fontSize: 13, margin: '0 0 12px' }}>
              Everyone who has used the tryout, newest activity first. Edit a visit count or status
              here instead of changing the database by hand.{' '}
              <strong>Lowering the visit count alone does not let a blocked visitor back in</strong> —
              Blocked is terminal in trial policy, so set Status to Active as well, or use Reopen.
            </p>

            {/* Filter row. One row above the table, as everywhere else in this console. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: '#6b7684', fontWeight: 600 }}>Status</span>
              <button
                className={`btn-small${statusFilter === 'All' ? ' btn-small--primary' : ''}`}
                onClick={() => setStatusFilter('All')}
              >
                All ({rows.length})
              </button>
              {counts.map(({ status, n }) => (
                <button
                  key={status}
                  className={`btn-small${statusFilter === status ? ' btn-small--primary' : ''}`}
                  onClick={() => setStatusFilter(status)}
                  disabled={n === 0}
                  title={n === 0 ? `No visitors are ${status}` : `Show only ${status}`}
                >
                  {status} ({n})
                </button>
              ))}
            </div>

            {msg && <div className="ov-flash" style={{ marginBottom: 10 }}>{msg}</div>}
            {err && (
              <div style={{
                marginBottom: 10, padding: '8px 12px', borderRadius: 8,
                background: '#fdeaea', color: '#a32020', fontSize: 13,
              }}>
                {err}
              </div>
            )}

            {loaded && rows.length === 0 && (
              <p style={{ color: '#6b7684', fontSize: 13, margin: 0 }}>
                No visitors yet. Open the tryout application once and refresh.
              </p>
            )}

            {loaded && rows.length > 0 && visible.length === 0 && (
              <p style={{ color: '#6b7684', fontSize: 13, margin: 0 }}>
                No visitors are {statusFilter}. <button className="btn-small" onClick={() => setStatusFilter('All')}>Show all</button>
              </p>
            )}

            {visible.length > 0 && (
              <div style={{ background: '#fff', border: '1px solid #e3e9ef', borderRadius: 10, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1180 }}>
                  <thead>
                    <tr>
                      {COLUMNS.map(c => {
                        const active = c.key === sortKey;
                        return (
                          <th
                            key={c.key}
                            style={{ ...th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                            onClick={() => sortBy(c)}
                            title={`Sort by ${c.label}`}
                            aria-sort={active ? (sortDesc ? 'descending' : 'ascending') : 'none'}
                          >
                            {c.label}
                            {/* An arrow, not just a colour — the sorted column has
                                to be identifiable without seeing hue. */}
                            <span style={{ color: active ? '#1b4f9c' : '#c7d0da', marginLeft: 4 }}>
                              {active ? (sortDesc ? '▼' : '▲') : '↕'}
                            </span>
                          </th>
                        );
                      })}
                      <th style={th} />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(row => {
                      const draft = drafts[row.trialId] ?? { visitCount: row.visitCount, status: row.status };
                      const busy = saving === row.trialId;
                      const tone = STATUS_TONE[row.status];

                      return (
                        <tr key={row.trialId}>
                          <td style={{ ...td, fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}
                              title={row.trialId}>
                            {shortId(row.trialId)}
                          </td>
                          <td style={td}>{row.platform || '—'}</td>
                          <td style={td}>
                            {row.browser || '—'}{row.browserVersion ? ` ${row.browserVersion}` : ''}
                          </td>
                          <td style={td}>
                            <input
                              style={{ ...inp, width: 70 }}
                              type="number"
                              min="0"
                              step="1"
                              value={draft.visitCount}
                              disabled={busy}
                              onChange={e => setDraft(row.trialId, { visitCount: Number(e.target.value) })}
                            />
                          </td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            {/* The pill shows the SERVER's current status; the select
                                below it is the pending edit. Showing only the select
                                would hide what the row actually is once it is dirty. */}
                            <span style={{
                              display: 'inline-block', padding: '1px 8px', borderRadius: 999,
                              fontSize: 11, fontWeight: 600, marginRight: 6,
                              color: tone.fg, background: tone.bg,
                            }}>
                              {row.status}
                            </span>
                            <select
                              style={{ ...txt, width: 168 }}
                              value={draft.status}
                              disabled={busy}
                              onChange={e => setDraft(row.trialId, { status: e.target.value as trialApi.TrialStatus })}
                            >
                              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </td>
                          {/* Two different timestamps, and the labels matter: lastVisit
                              is when the current session began (the session-window
                              anchor), updatedOn is when the row was last written. */}
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{when(row.lastVisit)}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{when(row.updatedOn)}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }} title={row.countryCode || undefined}>
                            {row.countryName || unknown()}
                          </td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{row.city || unknown()}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                            <button
                              className="btn-small"
                              onClick={() => save(row.trialId)}
                              disabled={busy || !dirty(row)}
                              title={dirty(row) ? 'Save the edited values' : 'Nothing changed'}
                            >
                              Save
                            </button>{' '}
                            <button
                              className="btn-small btn-small--primary"
                              onClick={() => reopen(row.trialId)}
                              disabled={busy}
                              title="Visit count 0, status Active, and the trial days restarted — in one call"
                            >
                              {busy ? '…' : 'Reopen'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <p style={{ color: '#94a1ad', fontSize: 12, margin: '10px 0 0' }}>
              Country and city are where the visitor's address geolocated to, and are frequently
              unknown — the provider can be off, can time out, and says nothing about a loopback
              address, which is every visitor on a developer machine. The address itself is hashed
              on arrival and is not stored. The list is capped server-side and has no paging.
            </p>
          </>
        )}
      </section>
    </div>
  );
};

/** Trial ids are long; the whole value is on the cell's title. */
const shortId = (trialId: string) => (trialId.length > 12 ? `${trialId.slice(0, 12)}…` : trialId);

/** ISO-8601 to something readable in the operator's own locale. */
const when = (iso: string) => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
};

/** Absent location, rendered as absent rather than as an empty cell. */
const unknown = () => <span style={{ color: '#a9b4bf' }}>unknown</span>;
